import React, { memo, useEffect } from 'react';
import Button from '../../../../global/atoms/button';
import Input from '../../../../global/atoms/input';
import PhoneNumber from '../../../../global/modules/formFields/phoneNumber';
import PasswordRules from '../../../../global/modules/password/PasswordRules';
import { useGuidedRegContext } from '../../../context/guidedRegContext';
import { useAnalyticsContext } from '../../../../../config/GoogleTagManagerEvents';
import { SET_CURRENT_INDEX } from '../../../context/reducer';
import { getAEMPersonalInfoData } from '../../utils/cashflowUtils';
import usePersonalFormState from '../../hooks/usePersonalFormState';
import { DATE_FORMAT, DATE_OF_BIRTH, FIRST_NAME, LAST_NAME, MOBILE_NUMBER, PASSWORD } from '../../constants';
import { grPersonalInfoShimmerEffect } from '../../../utils/shimmerUtil';
import { addFormReloadWarning } from '../../../utils/guidedUtil';
import { PersonalInfoTestId } from '../../../GRTestId';
import { formErrorFields, personalInfoFormErrorMapping } from '../../utils/formErrorMappingUtil';
import SummarizeFormErrors from '../../../../global/modules/SummarizeErrors';
import CustomDatePicker from '../../../../global/atoms/CustomDatePicker';
import { STORAGE_CONFIG } from '../../../../../constants/storageConfig';
import MobileModal from '../mobileModal';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../../../constants/analyticsConstants/Ecommerce';

const PersonalInfo = () => {
    const { commonConfig = {}, personalScreenConfig = {} } = getAEMPersonalInfoData() || {};
    const [, guidedRegDispatch] = useGuidedRegContext();
    const {
        isFormSubmit = false,
        unsavedChanges = false,
        setValidPassword = () => {},
        personalState = {},
        handleInputChange = () => {},
        handleDateChange = () => {},
        handleFocus = () => {},
        handleSubmit = () => {},
        onContinue = () => {},
        validateDate = () => {}
    } = usePersonalFormState();

    const { requiredFieldsLabel = '', continueButtonLabel = '', errorMessageLabel = '' } = commonConfig || {};

    const {
        personalInfoTitle = '',
        yourDetailsLabel = '',
        firstNameLabel = '',
        lastNameLabel = '',
        dateOfBirthLabel = '',
        dateOfBirthDisclaimer = '',
        mobileNumberLabel = '',
        mobileNumberDisclaimer = '',
        passwordSectionTitle = '',
        passwordDisclaimer = '',
        passwordFieldLabel = '',
        passwordRules = ''
    } = personalScreenConfig || {};

    const errorFields = formErrorFields(personalState?.error, personalScreenConfig, personalInfoFormErrorMapping) || [];
    const { sendEventsForPageViewed } = useAnalyticsContext();

    useEffect(() => {
        grPersonalInfoShimmerEffect();
        guidedRegDispatch({ type: SET_CURRENT_INDEX, payload: 0 });
        sendEventsForPageViewed(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_PAGE_VIEWED,
            personalInfoTitle,
            window.location.href
        );
    }, []);

    useEffect(() => {
        const cleanup = addFormReloadWarning(unsavedChanges);
        return cleanup;
    }, [unsavedChanges]);

    return (
        <section className={`stepform__section`}>
            <div className="container">
                <div className="row stepform__row">
                    <div className="col-lg-6">
                        <div className="form-heading stepform__heading">
                            <div className="stepform__steps" tabIndex="0">
                                <strong>Step 1 </strong>of 2
                            </div>
                            <h2 className="stepform__title">{personalInfoTitle}</h2>
                            {errorFields.length > 1 && (
                                <SummarizeFormErrors errorFields={errorFields} authorableError={errorMessageLabel} />
                            )}
                            <small className="stepform__require-field-text">{requiredFieldsLabel}</small>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <fieldset className={`stepform__fieldset`}>
                                <legend className={`stepform__legend`}>{yourDetailsLabel}</legend>
                                <div className="stepform__content">
                                    <Input
                                        name={FIRST_NAME}
                                        value={personalState?.data?.[FIRST_NAME]}
                                        handleInputChange={handleInputChange}
                                        label={firstNameLabel}
                                        inputAriaRequired={true}
                                        required={true}
                                        maxLength={50}
                                        showErrorIcon={true}
                                        errorMsg={personalState?.error?.[FIRST_NAME]}
                                        onFocus={() => handleFocus(FIRST_NAME)}
                                        dataTestId={PersonalInfoTestId.PERSONAL_INFO_FIRST_NAME}
                                    />
                                    <Input
                                        name={LAST_NAME}
                                        value={personalState?.data?.[LAST_NAME]}
                                        maxLength={50}
                                        handleInputChange={handleInputChange}
                                        label={lastNameLabel}
                                        inputAriaRequired={true}
                                        required={true}
                                        showErrorIcon={true}
                                        errorMsg={personalState?.error?.[LAST_NAME]}
                                        onFocus={() => handleFocus(LAST_NAME)}
                                        dataTestId={PersonalInfoTestId.PERSONAL_INFO_LAST_NAME}
                                    />

                                    <CustomDatePicker
                                        datePickerClassName="datepicker_wrapper"
                                        label={dateOfBirthLabel}
                                        name={DATE_OF_BIRTH}
                                        dateFormat={DATE_FORMAT}
                                        value={personalState?.data?.[DATE_OF_BIRTH]}
                                        validateDate={validateDate}
                                        onChange={handleDateChange}
                                        isVisible={false}
                                        onFocus={() => handleFocus(DATE_OF_BIRTH)}
                                        errorMsg={personalState?.error?.[DATE_OF_BIRTH]}
                                        required={true}
                                        disclaimer={dateOfBirthDisclaimer}
                                        id={DATE_OF_BIRTH}
                                    />

                                    <div className="cmp-Field__field__root">
                                        <PhoneNumber
                                            name={MOBILE_NUMBER}
                                            label={mobileNumberLabel}
                                            value={personalState?.data?.[MOBILE_NUMBER]}
                                            placeholder=""
                                            handleInputChange={handleInputChange}
                                            errorMessage={personalState?.error?.[MOBILE_NUMBER]}
                                            onFocus={() => handleFocus(MOBILE_NUMBER)}
                                            required={true}
                                            dataTestId={PersonalInfoTestId.PERSONAL_INFO_PHONE_NUMBER}
                                        />

                                        <div className="mobile-tooltip__info">
                                            <small className="mobile-tooltip__info-text" tabIndex="0">
                                                {mobileNumberDisclaimer}
                                            </small>

                                            <MobileModal />
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                            <fieldset className={`stepform__fieldset`}>
                                <legend className={`stepform__legend`}>{passwordSectionTitle}</legend>
                                <div className="stepform__content">
                                    <PasswordRules
                                        email={sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.GR_EMAIL)}
                                        passwordDisclaimer={passwordDisclaimer}
                                        passwordFieldLabel={passwordFieldLabel}
                                        passwordRules={passwordRules}
                                        firstName={personalState?.data?.[FIRST_NAME]}
                                        lastName={personalState?.data?.[LAST_NAME]}
                                        password={personalState?.data?.[PASSWORD]}
                                        errorPassword={personalState?.error?.[PASSWORD]}
                                        isFormSubmit={isFormSubmit}
                                        setValidPassword={setValidPassword}
                                        handleInputChange={handleInputChange}
                                        onFocus={() => handleFocus(PASSWORD)}
                                        required={true}
                                    />
                                </div>
                            </fieldset>
                            <Button
                                className={`button button-primary button-block `}
                                type="button"
                                onClick={onContinue}
                                dataTestid="continue-button"
                                buttonAriaLabel="Continue">
                                {continueButtonLabel}
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default memo(PersonalInfo);
